import React, { useContext, useMemo, useCallback } from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import { ReportingContext } from '../../../../wrappers/ReportingContext'
import CollapsibleContainer from '../CollapsibleContainer'
import InputField, { InputFieldSimple } from './InputField'
import ReportSelect, { selectStyles } from '../../../select/ReportSelect'
import CreatableSelect from 'react-select/creatable'
import {
    validateUniqueReportName,
    filterDataSourceOptions,
} from '../../../../../utils/reporting/config'

const CreateReport = () => {
    const {
        reportSelectOptions,
        reports,
        report,
        changeTargetDatabase,
        viewOnly = false
    } = useContext(ReportingContext)

    const {
        register,
        control,
        formState: { errors },
        watch,
        setValue,
    } = useFormContext()

    const selectedDatabase = watch('databaseName')
    const selectedPrimarySource = watch('primaryDataSourceName')
    
    const databaseOptions = useMemo(
        () => reportSelectOptions?.databases || [],
        [reportSelectOptions]
    )
    const tagOptions = useMemo(
        () => reportSelectOptions?.tags || [],
        [reportSelectOptions]
    )
    const primarySourceOptions = useMemo(
        () => filterDataSourceOptions(reportSelectOptions?.dataSources),
        [reportSelectOptions]
    )

    const onChangeTargetDatabase = useCallback(
        value => {
            changeTargetDatabase(value)
            setValue('primaryDataSourceName', null)
        },
        [changeTargetDatabase, setValue]
    )

    const onClearTargetDatabase = useCallback(() => {
        setValue('primaryDataSourceName', null)
    }, [setValue])

    return (
        <CollapsibleContainer title='Create Report' defaultOpen>
            <div className='content'>
                <InputFieldSimple
                    label={
                        <span>
                            Report Name{' '}
                            <span className='has-text-danger'>*</span>
                        </span>
                    }
                    name='reportName'
                    viewOnly={viewOnly}
                    inputProps={{
                        className: 'input is-normal',
                        name: 'reportName',
                        disabled: viewOnly,
                        placeholder: 'Enter a unique report name',
                        ref: register({
                            required: 'Required field',
                            validate: value =>
                                validateUniqueReportName(value, reports, report),
                        }),
                    }}
                    error={errors.reportName?.message}
                    helpText='Enter a unique name for your report'
                />

                <InputField
                    label={
                        <span>
                            Report Description <span className="has-text-danger">*</span>
                        </span>
                    }
                    name='reportDescription'
                    error={errors.reportDescription?.message}
                    helpText="Required description for your report"
                >
                    <textarea
                        className='textarea is-normal'
                        name='reportDescription'
                        disabled={viewOnly}
                        placeholder="Describe your report"
                        ref={register({
                            required: 'Required field'
                        })}
                    />
                </InputField>

                <InputField
                    label={
                        <span>
                            Database <span className='has-text-danger'>*</span>
                        </span>
                    }
                    name='databaseName'
                    error={errors.databaseName?.message}
                    helpText='Select the database for your report'
                >
                    <ReportSelect
                        name='databaseName'
                        control={control}
                        rules={{ required: 'Required field' }}
                        onOptionChange={onChangeTargetDatabase}
                        onClear={onClearTargetDatabase}
                        options={databaseOptions}
                        disabled={viewOnly}
                        value={selectedDatabase?.value}
                    />
                </InputField>

                <InputField
                    label={
                        <span>
                            Primary Source{' '}
                            <span className='has-text-danger'>*</span>
                        </span>
                    }
                    name='primaryDataSourceName'
                    helpText='Select the primary data source for your report'
                >
                    <ReportSelect
                        name='primaryDataSourceName'
                        control={control}
                        rules={{ required: 'Required field' }}
                        options={primarySourceOptions}
                        disabled={viewOnly || !selectedDatabase}
                        value={selectedPrimarySource?.value}
                    />
                </InputField>

                <InputField label='Tags' name='tags'>
                    <Controller
                        as={
                            <CreatableSelect
                                isMulti
                                styles={selectStyles}
                                options={tagOptions}
                                isDisabled={viewOnly}
                            />
                        }
                        onChange={([value]) => {
                            return value
                        }}
                        name='tags'
                        control={control}
                    />
                </InputField>

                <div
                    className='field is-grouped is-grouped-centered'
                    style={{ marginTop: '2rem' }}
                >
                    <div className='control'>
                        <button
                            type='submit'
                            className='button is-link'
                        >
                            Continue
                        </button>
                    </div>
                </div>
            </div>
        </CollapsibleContainer>
    )
}

export default CreateReport
